import React from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Navigate } from 'react-router-dom';
import { FeatureFlags } from '@repo/common-types';
import ROUTES from '@/routes/routes-enums';

interface FeatureFlagRouteProps {
  /** The feature flag to check */
  featureFlag: FeatureFlags;
  /** The component to render if the feature flag is enabled */
  component: React.ReactElement;
  /** Optional redirect path if feature flag is disabled (defaults to root) */
  redirectTo?: string;
  /** Optional fallback component to render if feature flag is disabled */
  fallbackComponent?: React.ReactElement;
}

/**
 * A route wrapper component that conditionally renders based on PostHog feature flags.
 *
 * @example
 * ```tsx
 * <FeatureFlagRoute
 *   featureFlag={FeatureFlags.AnalyticsBetaDashboard}
 *   component={<AnalyticsBeta />}
 *   redirectTo={ROUTES.ROOT}
 * />
 * ```
 */
const FeatureFlagRoute: React.FC<FeatureFlagRouteProps> = ({
  featureFlag,
  component,
  redirectTo = ROUTES.ROOT,
  fallbackComponent,
}) => {
  const isFeatureEnabled = useFeatureFlagEnabled(featureFlag);

  // If feature is disabled, show fallback or redirect
  if (!isFeatureEnabled) {
    if (fallbackComponent) {
      return fallbackComponent;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Feature is enabled, render the component
  return component;
};

export default FeatureFlagRoute;
